// clients.client_store_access read/write helpers. Lets an org operator
// decide which stores can see / serve a given client (the org-level
// clients refactor in 20260623030000 made store_id nullable on
// clients.clients and moved the per-store visibility model here).

import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';

/** Cast for cross-schema query against `clients.client_store_access`. */
const sbUntyped = (): any => supabase() as unknown as any; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface ClientStoreAccess {
  id: string;
  org_id: string;
  client_id: string;
  store_id: string;
  is_primary: boolean;
  granted_by: string | null;
  granted_at: string;
  archived_at: string | null;
}

export async function listClientStoreAccess(
  clientId: string,
): Promise<ClientStoreAccess[]> {
  const { data, error } = await sbUntyped()
    .schema('clients')
    .from('client_store_access')
    .select('*')
    .eq('client_id', clientId)
    .is('archived_at', null)
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as ClientStoreAccess[];
}

export async function grantClientStoreAccess(
  ctx: ActiveContext,
  input: { client_id: string; store_id: string; is_primary?: boolean },
): Promise<ClientStoreAccess> {
  const { orgId } = requireContext(ctx);
  const sb = sbUntyped();
  // Uniqueness is enforced by a partial index (archived_at IS NULL). PostgREST
  // upsert onConflict cannot target partial indexes, so we select-then-insert or
  // reactivate — same pattern as migrate.rpc_import_client_store_access.
  const { data: active, error: activeErr } = await sb
    .schema('clients')
    .from('client_store_access')
    .select('*')
    .eq('client_id', input.client_id)
    .eq('store_id', input.store_id)
    .is('archived_at', null)
    .maybeSingle();
  if (activeErr) throw activeErr;
  if (active) {
    if (input.is_primary) await setPrimaryStore(input.client_id, active.id);
    return active as unknown as ClientStoreAccess;
  }

  const { data: archivedRows, error: archivedErr } = await sb
    .schema('clients')
    .from('client_store_access')
    .select('*')
    .eq('client_id', input.client_id)
    .eq('store_id', input.store_id)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(1);
  if (archivedErr) throw archivedErr;
  const archived = (archivedRows ?? [])[0] as ClientStoreAccess | undefined;
  if (archived) {
    const { data, error } = await sb
      .schema('clients')
      .from('client_store_access')
      .update({
        archived_at: null,
        archived_by: null,
        is_primary: input.is_primary ?? archived.is_primary,
      })
      .eq('id', archived.id)
      .select()
      .single();
    if (error) throw error;
    if (input.is_primary) await setPrimaryStore(input.client_id, archived.id);
    return data as unknown as ClientStoreAccess;
  }

  const { data, error } = await sb
    .schema('clients')
    .from('client_store_access')
    .insert({
      org_id: orgId,
      client_id: input.client_id,
      store_id: input.store_id,
      is_primary: input.is_primary ?? false,
    })
    .select()
    .single();
  if (error) throw error;
  if (input.is_primary) await setPrimaryStore(input.client_id, data.id);
  return data as unknown as ClientStoreAccess;
}

export async function revokeClientStoreAccess(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('clients')
    .from('client_store_access')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function setPrimaryStore(
  clientId: string,
  storeAccessId: string,
): Promise<void> {
  // Demote all current rows for this client, then promote the chosen one.
  const sb = sbUntyped();
  await sb
    .schema('clients')
    .from('client_store_access')
    .update({ is_primary: false })
    .eq('client_id', clientId)
    .is('archived_at', null);
  const { error } = await sb
    .schema('clients')
    .from('client_store_access')
    .update({ is_primary: true })
    .eq('id', storeAccessId);
  if (error) throw error;
}
